import SideCartEditComponent from '@/components/EditOrder/SideCartComponent.jsx';
import { ModalContext } from '@/contexts/ModalProvider.jsx';
import { CurrencyContext } from '@/contexts/CurrencyProvider';
import { QueryKeys } from '@/hooks/useOrders.js';
import { getDifferenceInMins } from '@/utils/dates/dates.js';
import cn from 'classnames';
import dayjs from 'dayjs';
import React, { useState, useRef, useEffect, useContext } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  SideSheet,
  ActionsSelector,
  LocalShipping,
  Delete,
  Add,
  ShoppingBag,
} from '@desquared/wave-components-library';
import Router, { useRouter } from 'next/router';
import { startOrderEdit, cancelOrderEdit } from '@/api/wave/orders';
import styles from './EditOrder.module.css';
import { getOrderItemsV31 } from '@/api/orders';
import { EditModeCart, cartPrice, isEditStarted, productsToKeep, FullModeEdit, productsForOrder } from '@/store/cart';
import { ProductDetailsAside } from '../ProductDetailsAside/ProductDetailsAside';
import { useTranslations } from 'next-intl';
import DeliveryPaymentDetails from '@/components/EditOrder/DeliveryPaymentDetails.jsx';
import { SearchProductAside } from '../SearchProductAside/SearchProductAside';
import { cancelOrderEditCart } from '@/api/wave/orders';
import { getOrFetchConfiguration } from '@/utils/getConfiguration';
import { FullEditCatalogue } from '../FullEditCatalogue/FullEditCatalogue';
import getLayoutProps from '@/utils/layout/layout';
import useDisableBodyScroll from '@/hooks/useDisableBodyScroll';
import { DeliveryServicesContext } from '@/contexts/DeliveryServicesContext';
import useBundleOffersSummary from '@/hooks/useBundleOffersSummary';
import { fetchCart } from '@/api/syncCart';
import { transformCart } from '@/utils/products/products';
import {
  IngredientsEditNavigationStore,
  IngredientsModalConfigurationStore,
  IngredientsModalStore,
  IngredientsStore,
} from '@/store/prefferenceModal';

const EXPIRATION_MODAL_DISPLAY_TIMESPAN_IN_MINS = 5;
const EXPIRATION_TIME_INTERVAL_IN_MS = 5 * 1000;
let EXPIRATION_TIMER = undefined;
export default function EditOrderAside({
  isOpen,
  onClose,
  editModes,
  selectedOrder,
  onUpdateOrder,
  categoriesFromFile,
}) {
  const queryClient = useQueryClient();
  const sideSheetRef = useRef();
  const { formatter } = useContext(CurrencyContext);
  const { openActionModal, close } = useContext(ModalContext);
  const router = useRouter();
  const [checkDetailsOnly, setCheckDetailsOnly] = useState(true);
  const [isAddOnly, setIsAddOnly] = useState(false);
  const [timerStartDate, setTimerStartDate] = useState();
  const [hasSeenExpirationWarning, setHasSeenExpirationWarning] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState('');
  const [selectedProductFinalPrice, setSelectedFinalPrice] = useState();
  const [selectedProductBeginPrice, setSelectedBeginPrice] = useState();
  const [isEditing, setIsEditing] = useState(false);
  const [navigationState, setNavigationState] = useState([`ACTIONS_SELECTION`]);
  const [editMode, setEditMode] = useState('');
  const [allowedModes, setAllowedModes] = useState([]);
  const [fullEdit, setFullEdit] = useState();
  const [editImage, setEditImage] = useState();
  const [cancelImage, setCancelImage] = useState();
  const [cancelEditImage, setCancelEditImage] = useState();
  const [timeframeImage, setTimeframeImage] = useState();
  const [defaultIngredientsArray, setDefaultIngredientsArray] = useState();
  const { selectedUserAddressIndex, getCollectionTypes } = useContext(DeliveryServicesContext);
  const { bundleOffersSummary } = useBundleOffersSummary();

  const t = useTranslations('common.asides.side_cart');
  const a = useTranslations('common.asides');

  const INITIAL_EDIT_MODE_OPTIONS = [
    {
      id: 'ADD_PRODUCTS',
      title: t('add_new_product'),
      icon: Add,
    },
    {
      id: 'FULL_EDIT',
      title: t('full_edit_header'),
      icon: ShoppingBag,
    },
    {
      id: 'UPDATE_DETAILS',
      title: t('update_details'),
      icon: LocalShipping,
    },
    { id: 'CANCEL', title: t('cancel_order'), icon: Delete },
  ];
  const activeModes = allowedModes.filter(mode => mode.visible && mode.enabled);

  const formattedEditModes = INITIAL_EDIT_MODE_OPTIONS.flatMap(i => {
    // Check if the current mode exists in activeModes
    const selectedMode = activeModes.find(mode => mode.type === i.id);

    if (!selectedMode && i.id !== 'CANCEL') {
      return []; // Skip this mode if it's not in activeModes or not enabled
    }

    return {
      ...i,
      editTimeoutInMinutes: editModes?.edit?.modes?.find(editMode => editMode.mode === i.id)?.editTimeoutInMinutes,
      isDanger: i.id === 'CANCEL',
      isDisabled:
        i.id === 'CANCEL'
          ? !editModes?.cancel?.eligible
          : !editModes?.edit?.modes?.find(selectedMode => selectedMode?.mode === i.id && selectedMode.eligible),
    };
  });

  const STEPS = {
    ACTIONS_SELECTION: {
      id: 'ACTIONS_SELECTION',
      modalHeaderTitle: t('edit_order'),
      hasClose: true,
      hasBack: false,
    },
    UPDATE_DETAILS: {
      id: 'UPDATE_DETAILS',
      modalHeaderTitle: t('update_details'),
      hasClose: true,
      hasBack: true,
      onClose: show => {
        showCancellationModal(show);
      },
    },
    FULL_EDIT: {
      id: 'FULL_EDIT',
      modalHeaderTitle: `📝 ${t('full_edit_header')}`,
      hasClose: true,
      hasBack: true,
      onClose: () => {
        showCancellationModal();
      },
    },
    SEARCH_PRODUCTS: {
      id: 'SEARCH_PRODUCTS',
      modalHeaderTitle: fullEdit ? `📝 ${t('full_edit_header')}` : t('search_products_header'),
      hasClose: true,
      hasBack: true,
      onClose: () => {
        showCancellationModal();
      },
    },
    PRODUCT_DETAILS: {
      id: 'PRODUCT_DETAILS',
      modalHeaderTitle: fullEdit ? `📝 ${t('full_edit_header')}` : t('product_details_header'),
      hasClose: true,
      hasBack: true,
      onClose: () => {
        showCancellationModal();
      },
    },
    ADD_PRODUCTS: {
      id: 'ADD_PRODUCTS',
      modalHeaderTitle: t('add_products_header'),
      hasClose: true,
      hasBack: true,
      onClose: () => {
        showCancellationModal();
      },
    },
  };

  const [selectedStep, setSelectedStep] = useState(STEPS.ACTIONS_SELECTION);

  useEffect(() => {
    //clear timer on unmount
    async function getConfiguration() {
      const configuration = await getOrFetchConfiguration();
      setAllowedModes(configuration.order?.modification?.edit?.modes);
    }
    async function getProps() {
      const layoutProps = await getLayoutProps({ headerProps: { selectedIndex: -1 } }, Router.locale);
      const image = layoutProps?.emptyStates?.edit_order_empty_state?.url
        ? layoutProps?.emptyStates?.edit_order_empty_state?.url
        : null;
      setEditImage(image);
      const imageMin = layoutProps?.emptyStates?.cancel_edit?.url ? layoutProps?.emptyStates?.cancel_edit?.url : null;
      setCancelImage(imageMin);
      const imageEdit = layoutProps?.emptyStates?.cancel_order_empty_state?.url
        ? layoutProps?.emptyStates?.cancel_order_empty_state?.url
        : null;
      setCancelEditImage(imageEdit);
      const timeframe = layoutProps?.emptyStates?.edit_order_timeframe?.url
        ? layoutProps?.emptyStates?.edit_order_timeframe?.url
        : null;
      setTimeframeImage(timeframe);
    }
    getProps();
    getConfiguration();
    return () => window.clearInterval(EXPIRATION_TIMER);
  }, []);

  useEffect(() => {
    if (selectedStep.id === 'ACTIONS_SELECTION') {
      setIsEditing(false);
      setTimerStartDate(undefined);
      setCheckDetailsOnly(true);
      setSelectedProductId(undefined);
      setNavigationState(['ACTIONS_SELECTION']);
    }
  }, [selectedStep?.id]);

  const handleClose = () => {
    isEditStarted.update(false);
    productsToKeep.release();

    cancelOrderEdit(selectedOrder?._id, EditModeCart.getValue());
    setTimerStartDate(undefined);
    const fetchCartData = async () => {
      const collectionTypes = getCollectionTypes();
      try {
        const result = await fetchCart();
        const validProducts = result?.data?.products?.filter(item => item?.productId); // Filter out invalid items
        // Transform cart data
        if (validProducts?.length > 0) {
          transformCart(
            validProducts,
            'none',
            collectionTypes,
            selectedUserAddressIndex,
            bundleOffersSummary,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            a
          );
        }
      } catch (error) {
        console.error('Failed to fetch cart data:', error);
      }
    };
    setTimeout(() => {
      onClose();
      if (process.env.NEXT_PUBLIC_SYNC_CART === 'true') {
        fetchCartData();
      } else {
        if (cartPrice.getValue() !== 0 && cartPrice.getValue() !== '0.00') return;
        const totalPrice = productsForOrder.getValue().reduce((total, obj) => {
          if (obj.hasOwnProperty('price')) {
            const price = parseFloat(obj.price);
            if (!isNaN(price)) {
              total += price;
            }
          } else if (obj.hasOwnProperty('finalPrice')) {
            const price = parseFloat(obj.finalPrice);
            if (!isNaN(price)) {
              total += price / 100;
            }
          }
          return total;
        }, 0);

        cartPrice.update(totalPrice.toFixed(2));
      }
    }, 300);
  };

  const { data: orderProducts } = useQuery({
    queryKey: ['order-products', selectedOrder?._id, router.locale],
    enabled: !!selectedOrder?._id,
    queryFn: async () => getOrderItemsV31({ orderId: selectedOrder?._id }),
    cacheTime: 0,
    staleTime: 0,
  });

  const startOrder = async orderModeId => {
    setEditMode(orderModeId);
    EditModeCart.update(orderModeId);
    await startOrderEdit(selectedOrder?._id, orderModeId);
  };

  const startOrderState = orderModeId => {
    setEditMode(orderModeId);
    EditModeCart.update(orderModeId);
  };

  useEffect(() => {
    // document.addEventListener('click', handleClickOutside, true);
    isEditStarted.update(true);

    const editMode$ = EditModeCart.subscribe(setEditMode);
    const fullEditMode$ = FullModeEdit.subscribe(setFullEdit);
    productsToKeep.update();
    return () => {
      // document.removeEventListener('click', handleClickOutside, false);
      editMode$.unsubscribe();
      fullEditMode$.unsubscribe();
    };
  }, []);

  // const handleClickOutside = e => {
  //   if (sideSheetRef.current && !sideSheetRef.current.contains(e.target)) {
  //     handleClose();
  //   }
  // };

  const handleStartTimer = () => {
    window.clearInterval(EXPIRATION_TIMER);

    const differenceInMins = getDifferenceInMins(new Date(), new Date(timerStartDate));
    if (differenceInMins <= EXPIRATION_MODAL_DISPLAY_TIMESPAN_IN_MINS && !hasSeenExpirationWarning) {
      showExpiresSoonModal();
      setHasSeenExpirationWarning(true);
      return;
    }

    if (differenceInMins <= 0 && hasSeenExpirationWarning) {
      showExpiredModal();
      setTimerStartDate(undefined);
      return;
    }

    EXPIRATION_TIMER = window.setInterval(() => {
      const differenceInMins = getDifferenceInMins(new Date(), new Date(timerStartDate));

      if (differenceInMins <= EXPIRATION_MODAL_DISPLAY_TIMESPAN_IN_MINS && !hasSeenExpirationWarning) {
        showExpiresSoonModal();
        window.clearInterval(EXPIRATION_TIMER);
        setHasSeenExpirationWarning(true);
        return;
      }

      if (differenceInMins <= 0 && hasSeenExpirationWarning) {
        showExpiredModal();
        setTimerStartDate(undefined);
      }
    }, EXPIRATION_TIME_INTERVAL_IN_MS);
  };

  useEffect(() => {
    if (timerStartDate) handleStartTimer();
  }, [timerStartDate]);

  const showExpiresSoonModal = () => {
    openActionModal({
      closeOnOuterClick: true,
      title: t('time_run_out_title'),
      text: t('time_run_out_soon_description', { mins: EXPIRATION_MODAL_DISPLAY_TIMESPAN_IN_MINS }),
      actions: [
        {
          btnLabel: t('return_to_cart'),
          onClick: () => {
            close();
          },
        },
      ],
      img: {
        url: timeframeImage ? timeframeImage : '/assets/wave-grocery/Bus.png',
        ariaLabel: t('time_run_out_title'),
        size: 'medium',
      },
    });
  };
  const showExpiredModal = () => {
    openActionModal({
      closeOnOuterClick: true,
      title: t('time_run_out_title'),
      text: t('time_run_out_description'),
      actions: [
        {
          btnLabel: t('return'),
          onClick: () => {
            handleClose();
            close();
          },
        },
      ],
      img: {
        url: timeframeImage ? timeframeImage : '/assets/wave-grocery/Bus.png',
        ariaLabel: t('time_run_out_title'),
        size: 'medium',
      },
    });
  };
  const showCancellationModal = (show = false) => {
    if (show) {
      handleClose();
      close();
    } else {
      let cancel = cancelImage ? cancelImage : '/assets/wave-grocery/Bus.png';
      openActionModal({
        closeOnOuterClick: true,
        title: `${t('cancel_edit')};`,
        text: t('cancel_edit_description'),
        actions: [
          {
            btnLabel: t('return_to_cart'),
            onClick: () => {
              close();
            },
          },
          {
            btnLabel: t('cancel_edit'),
            btnVariant: 'text',
            onClick: () => {
              handleClose();
              close();
            },
          },
        ],
        img: {
          url: cancel,
          ariaLabel: t('cancel_edit'),
          size: 'medium',
        },
      });
    }
  };

  const clearCachedQueryData = async () => {
    await queryClient.invalidateQueries({ queryKey: QueryKeys.GetOrderEditModes(selectedOrder?._id) });
  };

  const handleSelectAction = async (orderModeId, checkDetailsOnly, isFromFullEdit = false) => {
    setIsEditing(true);
    setNavigationState([...navigationState, orderModeId]);

    if (orderModeId == 'FULL_EDIT') {
      setFullEdit(true);
      FullModeEdit.update(true);
    } else if (orderModeId == 'ACTIONS_SELECTION') {
      setFullEdit(false);
      FullModeEdit.update(false);
    }

    switch (orderModeId) {
      case 'ADD_PRODUCTS':
        setCheckDetailsOnly(checkDetailsOnly);
        setIsAddOnly(true);
        startOrder(orderModeId);
        setSelectedStep(STEPS.ADD_PRODUCTS);
        break;
      case 'FULL_EDIT':
        //don't show the modal if we have already accepted it

        if (isEditing) {
          setCheckDetailsOnly(checkDetailsOnly);
          setSelectedStep(STEPS.FULL_EDIT);
          startOrder(orderModeId);
          return;
        }
        let image = editImage ? editImage : '/assets/wave-grocery/BananaWorried.png';
        openActionModal({
          closeOnOuterClick: true,
          title: t('refresh_prices_title'),
          text: t('refresh_prices_description'),
          actions: [
            {
              btnLabel: t('continue'),
              onClick: () => {
                setCheckDetailsOnly(checkDetailsOnly);
                setSelectedStep(STEPS.FULL_EDIT);
                close();
                startOrder(orderModeId);
              },
            },
            {
              btnLabel: t('close'),
              btnVariant: 'text',
              onClick: () => {
                setSelectedStep(STEPS.ACTIONS_SELECTION);
                close();
              },
            },
          ],
          img: {
            url: image,
            ariaLabel: t('time_ran_out_error.img_aria_label'),
            size: 'medium',
          },
        });

        break;
      case 'UPDATE_DETAILS':
        setSelectedStep(STEPS.UPDATE_DETAILS);
        if (isFromFullEdit) {
          startOrderState(orderModeId);
        } else {
          startOrder(orderModeId);
        }
        break;
      case 'SEARCH_PRODUCTS':
        setSelectedStep(STEPS.SEARCH_PRODUCTS);
        break;
      case 'PRODUCT_DETAILS':
        setSelectedStep(STEPS.PRODUCT_DETAILS);
        break;
      case 'CANCEL':
        let cancel = cancelEditImage ? cancelEditImage : '/assets/wave-grocery/Falled_Jenga.png';
        openActionModal({
          closeOnOuterClick: true,
          title: t('cancel_title'),
          text: t('cancel_description_first'),
          actions: [
            {
              btnLabel: t('cancel_revert'),
              onClick: () => {
                close();
              },
            },
            {
              btnLabel: t('cancel_continue'),
              btnVariant: 'text',
              onClick: () => {
                cancelOrderEditCart(selectedOrder?._id).then(async () => {
                  close();
                  isEditStarted.update(false);

                  await clearCachedQueryData();
                  openActionModal({
                    closeOnOuterClick: false,
                    title: t('cancel_title'),
                    text: t('cancel_description_second'),
                    actions: [
                      {
                        btnLabel: t('cancel_confirm'),
                        onClick: async () => {
                          close();
                          await router.push('/');
                        },
                      },
                    ],
                    img: {
                      url: cancel,
                      ariaLabel: t('cancel_description_second'),
                      size: 'medium',
                    },
                  });
                });
                close();
              },
            },
          ],
          img: {
            url: cancelEditImage ? cancelEditImage : '/assets/wave-grocery/Falling_Jenga.png',
            ariaLabel: t('cancel_description_first'),
            size: 'medium',
          },
        });

        break;
    }
  };
  useDisableBodyScroll();
  const handleProductSelect = (id, finalPrice, beginPrice, defaultIngredientArray) => {
    if (defaultIngredientArray) {
      setDefaultIngredientsArray(defaultIngredientArray);
    }

    setSelectedProductId(id);
    setSelectedFinalPrice(finalPrice);
    setSelectedBeginPrice(beginPrice);
    handleSelectAction('PRODUCT_DETAILS');
  };

  IngredientsEditNavigationStore.update(handleProductSelect);

  const renderStep = () => {
    switch (selectedStep.id) {
      case 'ACTIONS_SELECTION':
        return (
          <ActionsSelector
            options={formattedEditModes}
            onSelect={orderModeId => {
              const selectedMode = formattedEditModes.find(i => i.id === orderModeId);
              //selecting an action with editTimeoutInMinutes should start a timer
              if (selectedMode.editTimeoutInMinutes) {
                setTimerStartDate(dayjs().add(selectedMode.editTimeoutInMinutes, 'm').toDate());
              }
              handleSelectAction(orderModeId);
            }}
          />
        );
      case 'UPDATE_DETAILS':
        return (
          <DeliveryPaymentDetails
            selectedOrder={selectedOrder}
            onClose={show => {
              showCancellationModal(show);
            }}
            checkDetailsOnly={checkDetailsOnly}
            onUpdateOrder={onUpdateOrder}
            isAddOnly={isAddOnly}
          />
        );
      case 'FULL_EDIT':
        return (
          <SideCartEditComponent
            selectedOrder={selectedOrder}
            orderProducts={orderProducts}
            onSelect={orderModeId => handleSelectAction(orderModeId, false, true)}
            onOpenDetails={handleProductSelect}
          />
        );
      case 'SEARCH_PRODUCTS':
        if (FullModeEdit.getValue()) {
          return (
            <FullEditCatalogue
              categoriesFromFile={categoriesFromFile}
              onSelect={handleProductSelectWithoutId}
              selectedOrder={selectedOrder}
            />
          );
        } else {
          return (
            <SearchProductAside
              onProductSelect={handleProductSelect}
              selectedOrder={selectedOrder}
              onSelectCheckout={orderModeId => handleSelectAction(orderModeId, false)}
              onBack={() => handleBackNavigation(true)}
              isAddOnly={true}
              orderProducts={orderProducts}
            />
          );
        }

      case 'PRODUCT_DETAILS':
        return (
          <ProductDetailsAside
            id={selectedProductId}
            finalPrice={selectedProductFinalPrice}
            beginPrice={selectedProductBeginPrice}
            defaultIngredientsArray={defaultIngredientsArray}
            onBack={handleBackNavigation}
            handleProductSelect={handleProductSelect}
          />
        );
      case 'ADD_PRODUCTS':
        return (
          <SearchProductAside
            onProductSelect={handleProductSelect}
            selectedOrder={selectedOrder}
            onSelectCheckout={orderModeId => handleSelectAction(orderModeId, true)}
            onBack={() => handleBackNavigation(true)}
            isAddOnly={true}
            orderProducts={orderProducts}
          />
        );
      default:
        return null;
    }
  };

  const handleBackNavigation = (isAddOnly = false) => {
    if (navigationState.length === 0) return;

    if (isAddOnly === true) {
      productsToKeep.release();
    }
    const navClone = [...navigationState];
    navClone.pop();

    setNavigationState(navClone);
    setSelectedStep(STEPS[navClone[navClone.length - 1]]);
  };
  const handleProductSelectWithoutId = product => {
    if (product?.id) {
      const productInCart = productsForOrder.getValue().find(p => p.id === product.id);
      if (
        productInCart &&
        productInCart.ingredients &&
        productInCart.ingredientsGroups?.length &&
        !IngredientsModalStore.getValue()
      ) {
        IngredientsModalConfigurationStore.update('small');
        IngredientsStore.update(product);
        IngredientsModalStore.update(true);
        return;
      }

      setSelectedProductId(product.id);
    } else {
      setSelectedProductId(product?._id);
    }

    handleSelectAction('PRODUCT_DETAILS');
  };

  if (!isOpen && !orderProducts) return null;

  return (
    <div ref={sideSheetRef}>
      <SideSheet
        header={selectedStep.modalHeaderTitle}
        hasClose={!!selectedStep.hasClose}
        onClose={selectedStep?.onClose || handleClose}
        hasBack={!!selectedStep.hasBack}
        onBack={handleBackNavigation}
        position="right"
        isInFullEdit={
          selectedStep.id === 'FULL_EDIT' ||
          selectedStep.id === 'SEARCH_PRODUCTS' ||
          selectedStep.id === 'PRODUCT_DETAILS'
        }
        isWide={(selectedStep.id === 'SEARCH_PRODUCTS' && fullEdit) || selectedStep.id === 'PRODUCT_DETAILS'}
        actionButtonText={
          (selectedStep.id === 'FULL_EDIT' ||
            selectedStep.id === 'SEARCH_PRODUCTS' ||
            selectedStep.id === 'PRODUCT_DETAILS') &&
          t('close')
        }
        buttonText={
          selectedStep.id === 'FULL_EDIT'
            ? t('secondary_button')
            : fullEdit &&
              selectedStep.id !== 'UPDATE_DETAILS' &&
              selectedStep.id !== 'ACTIONS_SELECTION' &&
              `${t('header')} ${formatter.format(cartPrice.getValue())}`
        }
        onAction={selectedStep?.onClose || handleClose}
        onSubmit={() => {
          if (selectedStep.id === 'FULL_EDIT') {
            handleSelectAction('SEARCH_PRODUCTS');
          } else if ((selectedStep.id === 'SEARCH_PRODUCTS' || selectedStep.id === 'PRODUCT_DETAILS') && fullEdit) {
            handleSelectAction('FULL_EDIT');
          }
        }}
        classOption={cn(styles.sideSheet)}
        isInEditOrder={true} // we do this in order for the open/close animation to be 0.5s
      >
        {selectedStep.id === 'SEARCH_PRODUCTS' ||
        selectedStep.id === 'PRODUCT_DETAILS' ||
        selectedStep.id === 'ADD_PRODUCTS' ? (
          renderStep()
        ) : (
          <div className={cn(selectedStep.id !== 'FULL_EDIT' ? styles.wrapper : styles.overflow)}>{renderStep()}</div>
        )}
      </SideSheet>
    </div>
  );
}
